import {
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  productForPath,
  useHasProductTabs,
} from "src/components/layout/nav/sections/hooks/projects";
import { setSettingsPopStateHandler } from "src/components/layout/settings-history";
import {
  type NavigateOptions,
  NavigationInterceptorContext,
  clearAppLevelNavigationInterceptor,
  setAppLevelNavigationInterceptor,
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
} from "src/router";
import { lazyNamed } from "src/utils/lazy-named";

const SettingsModal = lazyNamed(
  () => import("src/components/layout/settings-modal"),
  "SettingsModal",
);

// Marks the history entry that shows a settings URL, so Back and Forward across it are handled here.
const SETTINGS_ENTRY = "htSettingsEntry";
// The workspace path of the page under settings, so a refresh or history move onto the entry restores that page behind them.
const PAGE_PATH = "htSettingsPagePath";

type HistoryState = {
  key?: string;
  [SETTINGS_ENTRY]?: boolean;
  [PAGE_PATH]?: string;
} | null;

type OpenSettings = {
  /** The workspace path, slug excluded, the modal opened on. */
  initialPath: string;
  /** True when settings pushed their own history entry over the page, false when they took over a deep link's entry. */
  hasOwnEntry: boolean;
  /** False when a page load or history move restored them, which shouldn't play the modal's entrance. */
  animateIn: boolean;
};

type SettingsModalProviderProps = {
  children: ReactNode;
};

const isSettingsWorkspacePath = (path: string) =>
  productForPath(`/workspace${path.split(/[?#]/)[0]}`) === "settings";

const historyState = () => window.history.state as HistoryState;

/**
 * Opens settings in a modal over the page without changing the app router's location, so the page underneath stays
 * mounted. The modal runs its own memory router, and its location is written to the address bar directly.
 */
export const SettingsModalProvider = ({
  children,
}: SettingsModalProviderProps) => (
  // Settings' own navigations, such as restoring the page under them, skip the app-level interceptor they register.
  <NavigationInterceptorContext.Provider value={null}>
    <SettingsModalHost>{children}</SettingsModalHost>
  </NavigationInterceptorContext.Provider>
);

const SettingsModalHost = ({ children }: SettingsModalProviderProps) => {
  const hasProductTabs = useHasProductTabs();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { root_workspace_slug: slug } = useParams<{
    root_workspace_slug: string;
  }>();
  const [open, setOpen] = useState<OpenSettings | null>(null);
  // The entry settings push copies the page's React Router key, so a move between the two is no move to the router.
  const pageKey = useRef(location.key);
  pageKey.current = location.key;
  // Where to go once Back has returned to the page, for links that leave settings.
  const pendingPath = useRef<string | null>(null);
  // Its options, such as an ad's return location, for that move.
  const pendingOptions = useRef<NavigateOptions | undefined>(undefined);
  const settingsUrl = useRef<string | null>(null);
  // The state of settings' history entry, to put it back when a settings page holds a history move.
  const settingsEntryState = useRef<HistoryState>(null);
  const requestCloseRef = useRef<
    ((path?: string, options?: NavigateOptions) => boolean) | null
  >(null);
  const pagePath = useRef("/home");
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const openRef = useRef(open);
  openRef.current = open;
  // Set while settings send the browser Back to the page themselves.
  const returningToPage = useRef(false);

  const isOnSettingsUrl =
    hasProductTabs && productForPath(location.pathname) === "settings";
  const pageUrl = `${location.pathname}${location.search}${location.hash}`;

  const showInAddressBar = useCallback((url: string) => {
    settingsUrl.current = url;
    settingsEntryState.current = {
      ...historyState(),
      [SETTINGS_ENTRY]: true,
      [PAGE_PATH]: pagePath.current,
    };
    window.history.replaceState(settingsEntryState.current, "", url);
  }, []);

  // A replacing navigation opens settings over the page's own entry, as a deep link does, so closing them doesn't go Back past it.
  const intercept = useCallback(
    (path: string, options?: NavigateOptions) => {
      if (!slug || !isSettingsWorkspacePath(path)) return false;
      const hasOwnEntry = !options?.replace;
      settingsUrl.current = `/${slug}${path}`;
      pagePath.current = pageUrl.slice(slug.length + 1);
      settingsEntryState.current = {
        ...historyState(),
        [SETTINGS_ENTRY]: true,
        [PAGE_PATH]: pagePath.current,
      };
      if (hasOwnEntry) {
        window.history.pushState(
          settingsEntryState.current,
          "",
          settingsUrl.current,
        );
      } else {
        window.history.replaceState(
          settingsEntryState.current,
          "",
          settingsUrl.current,
        );
      }
      setOpen({ initialPath: path, hasOwnEntry, animateIn: true });
      return true;
    },
    [slug, pageUrl],
  );

  // A pending settings path reopens settings over the page Back returned to.
  const interceptRef = useRef(intercept);
  interceptRef.current = intercept;

  // A settings URL loaded or routed to directly opens over the page its entry recorded, or else home, keeping its own entry and address.
  useEffect(() => {
    if (!isOnSettingsUrl || !slug) return;
    const path = pageUrl.slice(slug.length + 1);
    const recordedPage = historyState()?.[PAGE_PATH];
    settingsUrl.current = pageUrl;
    pagePath.current =
      recordedPage && !isSettingsWorkspacePath(recordedPage)
        ? recordedPage
        : "/home";
    setOpen({
      initialPath: path,
      hasOwnEntry: false,
      animateIn: navigationType !== "POP",
    });
    navigate(pagePath.current, { replace: true });
  }, [isOnSettingsUrl, pageUrl, slug, navigate, navigationType]);

  // Puts the settings URL back after the app router replaces the entry with home.
  useEffect(() => {
    if (open && !isOnSettingsUrl && settingsUrl.current) {
      showInAddressBar(settingsUrl.current);
    }
  }, [open, isOnSettingsUrl, showInAddressBar]);

  // Runs before React Router's listener, which would read a move between settings' entry and the page's as a navigation
  // to the page it already shows and, with an unsaved-changes blocker up, undo it by reloading.
  useEffect(() => {
    if (!hasProductTabs) return;
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state as HistoryState;
      // React Router leaves the session's first entry without a key and calls it "default".
      const isPageEntry = (state?.key ?? "default") === pageKey.current;

      // Onto a settings entry, settings reopen; when React Router takes the move, the settings URL restores the page behind.
      if (state?.[SETTINGS_ENTRY]) {
        if (isPageEntry) event.stopImmediatePropagation();
        const { pathname, search, hash } = window.location;
        settingsUrl.current = `${pathname}${search}${hash}`;
        settingsEntryState.current = state;
        pagePath.current = state[PAGE_PATH] ?? pagePath.current;
        setOpen({
          initialPath: settingsUrl.current.slice((slug?.length ?? 0) + 1),
          hasOwnEntry: true,
          animateIn: true,
        });
        return;
      }

      const isReturning = returningToPage.current;
      returningToPage.current = false;
      const current = openRef.current;

      // A settings page with unsaved changes holds settings open: their entry goes back on top and the page asks the viewer.
      // Leaving then closes settings as their close button does, going Back from that entry.
      if (
        current &&
        !isReturning &&
        settingsUrl.current &&
        requestCloseRef.current?.() === false
      ) {
        event.stopImmediatePropagation();
        window.history.pushState(
          settingsEntryState.current,
          "",
          settingsUrl.current,
        );
        setOpen({ ...current, hasOwnEntry: true });
        return;
      }

      // Off settings' own entry onto the page's, settings close and the router stays put; any other move is the router's.
      if (isPageEntry && (isReturning || current?.hasOwnEntry)) {
        event.stopImmediatePropagation();
        setOpen(null);
        const path = pendingPath.current;
        const options = pendingOptions.current;
        pendingPath.current = null;
        pendingOptions.current = undefined;
        if (path && !interceptRef.current(path, options)) {
          navigateRef.current(path, options);
        }
        return;
      }
      if (current) setOpen(null);
    };
    setSettingsPopStateHandler(handlePopState);
    return () => setSettingsPopStateHandler(null);
  }, [hasProductTabs, slug]);

  const leaveSettings = useCallback(
    (path: string | null, options?: NavigateOptions) => {
      if (!open) return;
      setOpen(null);
      settingsUrl.current = null;
      if (open.hasOwnEntry) {
        pendingPath.current = path;
        pendingOptions.current = options;
        returningToPage.current = true;
        window.history.back();
        return;
      }
      window.history.replaceState(
        {
          ...historyState(),
          [SETTINGS_ENTRY]: undefined,
          [PAGE_PATH]: undefined,
        },
        "",
        pageUrl,
      );
      if (path) navigate(path, options);
    },
    [open, pageUrl, navigate],
  );

  // Navigations from above the layout, such as the command palette's, can't see settings' context, so settings handle them here.
  // Open settings move to the path named through their own router, so a settings page with unsaved changes can hold the move.
  useEffect(() => {
    if (!hasProductTabs) return;
    const interceptAppLevel = (path: string, options?: NavigateOptions) => {
      if (!openRef.current) return intercept(path, options);
      const requestClose = requestCloseRef.current;
      if (requestClose) requestClose(path, options);
      else leaveSettings(path, options);
      return true;
    };
    setAppLevelNavigationInterceptor(interceptAppLevel);
    return () => clearAppLevelNavigationInterceptor(interceptAppLevel);
  }, [hasProductTabs, intercept, leaveSettings]);

  return (
    <NavigationInterceptorContext.Provider
      value={hasProductTabs ? intercept : null}
    >
      {children}
      {open && slug && (
        <Suspense fallback={null}>
          <SettingsModal
            initialUrl={`/${slug}${open.initialPath}`}
            animateIn={open.animateIn}
            onNavigate={showInAddressBar}
            onClose={() => leaveSettings(null)}
            onLeave={leaveSettings}
            requestCloseRef={requestCloseRef}
          />
        </Suspense>
      )}
    </NavigationInterceptorContext.Provider>
  );
};
